"use client"

import { cloneElement, ReactElement, useRef, useState } from "react"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@ui/shad"
import { Link2, Upload } from "lucide-react"
import { toast } from "sonner"

import { StorageAttachmentRecordType, useCreateStorageAttachment } from "@/hooks/api/storage_attachments"
import { useStorageIntegrations } from "@/hooks/api/storage_integrations"

import { STORAGE_PROVIDER_NAMES, StorageLinkPickerDialog, StorageLinkSelection } from "./storage-link-picker-dialog"

export const ATTACHMENT_MAX_FILES = 20
export const ATTACHMENT_MAX_FILE_SIZE = 20 * 1024 * 1024
export const ATTACHMENT_ALLOWED_FILE_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/plain",
  "text/csv",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
  "image/png",
  "image/jpeg",
  "image/gif",
]

interface TriggerProps {
  onClick?: () => void
  disabled?: boolean
}

interface AttachmentAddMenuProps {
  children: ReactElement<TriggerProps>
  onFilesSelected: (files: File[]) => void
  existingCount: number
  storageLink?: {
    recordType: StorageAttachmentRecordType
    recordId: string
    allowCopyPerInstance?: boolean
  }
  disabled?: boolean
  onLinkAttached?: () => void
}

// Single entry point for adding attachments: a dropdown offering "Upload file" and
// "Attach from <provider>" when the book has a usable storage integration, degrading
// to a plain file-picker trigger when it doesn't.
export function AttachmentAddMenu({
  children,
  onFilesSelected,
  existingCount,
  storageLink,
  disabled = false,
  onLinkAttached,
}: AttachmentAddMenuProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [pickerOpen, setPickerOpen] = useState(false)
  const { data: integrations } = useStorageIntegrations()
  const { mutate: createAttachment, isPending: isAttaching } = useCreateStorageAttachment()

  const integration = integrations?.find((i) => i.is_active && i.auth_status === "valid")
  const showLinkOption = !!integration && !!storageLink

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    if (fileInputRef.current) fileInputRef.current.value = ""
    if (files.length === 0) return

    const remainingSlots = ATTACHMENT_MAX_FILES - existingCount
    if (files.length > remainingSlots) {
      toast.error(`You can only add ${remainingSlots} more file(s). Maximum is ${ATTACHMENT_MAX_FILES}.`)
      return
    }

    for (const file of files) {
      if (file.size > ATTACHMENT_MAX_FILE_SIZE) {
        toast.error(`${file.name} is too large. Maximum size is 20MB.`)
        return
      }
      if (!ATTACHMENT_ALLOWED_FILE_TYPES.includes(file.type)) {
        toast.error(`${file.name} has an unsupported file type.`)
        return
      }
    }

    onFilesSelected(files)
  }

  const handleAttachLink = (selection: StorageLinkSelection) => {
    if (!storageLink || !integration) return

    createAttachment(
      {
        recordType: storageLink.recordType,
        recordId: storageLink.recordId,
        storageIntegrationId: integration.id,
        attachment: selection,
      },
      {
        onSuccess: () => {
          toast.success(selection.kind === "folder" ? "Folder linked" : "File linked")
          setPickerOpen(false)
          onLinkAttached?.()
        },
      }
    )
  }

  const fileInput = (
    <input
      ref={fileInputRef}
      type="file"
      multiple
      accept={ATTACHMENT_ALLOWED_FILE_TYPES.join(",")}
      onChange={handleFileSelect}
      className="hidden"
    />
  )

  if (!showLinkOption) {
    return (
      <>
        {cloneElement(children, { onClick: () => fileInputRef.current?.click(), disabled })}
        {fileInput}
      </>
    )
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild disabled={disabled || isAttaching}>
          {children}
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem onSelect={() => fileInputRef.current?.click()}>
            <Upload />
            Upload file
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setPickerOpen(true)}>
            <Link2 />
            Attach from {STORAGE_PROVIDER_NAMES[integration.type]}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {fileInput}

      <StorageLinkPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        integration={integration}
        allowCopyPerInstance={storageLink.allowCopyPerInstance ?? false}
        onAttach={handleAttachLink}
        isAttaching={isAttaching}
      />
    </>
  )
}
